import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import Select from './Select';

const GameControlInterface = ({ 
  isOpen = false, 
  onClose, 
  onModeChange, 
  onManualSpin,
  currentMode = 'automatic',
  isSpinning = false 
}) => {
  const [selectedMode, setSelectedMode] = useState(currentMode);
  const [position, setPosition] = useState({ x: 20, y: 20 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  const modeOptions = [
    { 
      value: 'automatic', 
      label: 'Automatic Mode',
      description: 'Participants spin independently'
    },
    { 
      value: 'assisted', 
      label: 'Assisted Mode',
      description: 'XAIKUS team controls spins'
    }
  ];

  const handleModeChange = (value) => {
    setSelectedMode(value);
    onModeChange(value);
  };

  const handleManualSpin = () => {
    if (!isSpinning && selectedMode === 'assisted') {
      onManualSpin();
    }
  };

  const handleMouseDown = (e) => {
    if (e?.target?.closest('.control-panel-header')) {
      setIsDragging(true);
      setDragOffset({
        x: e?.clientX - position?.x,
        y: e?.clientY - position?.y
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      const newX = Math.max(0, Math.min(window.innerWidth - 320, e?.clientX - dragOffset?.x));
      const newY = Math.max(0, Math.min(window.innerHeight - 400, e?.clientY - dragOffset?.y));
      setPosition({ x: newX, y: newY });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed z-1000 w-80 bg-card rounded-xl shadow-2xl border border-border animate-scale-in"
      style={{
        left: `${position?.x}px`,
        top: `${position?.y}px`,
        cursor: isDragging ? 'grabbing' : 'default'
      }}
      onMouseDown={handleMouseDown}
    >
      <div className="control-panel-header flex items-center justify-between p-4 border-b border-border cursor-grab active:cursor-grabbing">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center">
            <Icon name="Gamepad2" size={20} color="var(--color-secondary)" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">Game Controls</h3>
            <p className="text-xs text-muted-foreground">Operator interface</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted transition-smooth focus-ring"
          aria-label="Close controls"
        >
          <Icon name="X" size={16} color="var(--color-muted-foreground)" />
        </button>
      </div>
      <div className="p-4 space-y-4">
        <div>
          <Select
            label="Operation Mode"
            description="Select how the roulette operates"
            options={modeOptions}
            value={selectedMode}
            onChange={handleModeChange}
          />
        </div>

        {selectedMode === 'assisted' && (
          <div className="space-y-3">
            <div className="p-3 bg-muted/50 rounded-lg border border-border">
              <div className="flex items-start gap-2">
                <Icon name="Info" size={16} color="var(--color-primary)" className="mt-0.5 flex-shrink-0" />
                <div className="text-sm text-foreground">
                  <p className="font-medium mb-1">Assisted Mode Active</p>
                  <p className="text-muted-foreground text-xs">
                    You control when the wheel spins. Participants cannot initiate spins independently.
                  </p>
                </div>
              </div>
            </div>

            <Button
              variant="default"
              fullWidth
              onClick={handleManualSpin}
              disabled={isSpinning}
              loading={isSpinning}
              iconName="Play"
              iconPosition="left"
              size="lg"
            >
              {isSpinning ? 'Spinning...' : 'Spin Wheel'}
            </Button>
          </div>
        )}

        {selectedMode === 'automatic' && (
          <div className="p-3 bg-success/10 rounded-lg border border-success/20">
            <div className="flex items-start gap-2">
              <Icon name="CheckCircle2" size={16} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
              <div className="text-sm text-foreground">
                <p className="font-medium mb-1">Automatic Mode Active</p>
                <p className="text-muted-foreground text-xs">
                  Participants can spin the wheel independently. No operator intervention required.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="pt-3 border-t border-border">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Wheel Status</span>
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${isSpinning ? 'bg-warning animate-pulse' : 'bg-success'}`} />
              <span className="font-medium text-foreground">
                {isSpinning ? 'Spinning' : 'Ready'}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="p-4 border-t border-border bg-muted/30">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Icon name="Move" size={12} />
          <span>Drag header to reposition</span>
        </div>
      </div>
    </div>
  );
};

export default GameControlInterface;